import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const HeaderContainer = styled.header`
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  padding: 1rem 0;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Logo = styled(Link)`
  color: var(--text);
  text-decoration: none;
  font-size: 1.5rem;
  font-weight: 700;
  
  span {
    color: var(--accent-turquoise);
  }
`;

const MenuItems = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  align-items: center;
  
  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: white;
    padding: 1rem 0;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  }
`;

const MenuItem = styled.li`
  margin: 0 1.5rem;
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  
  &:first-child {
    margin-left: 0;
  }
  
  &:last-child {
    margin-right: 0;
  }
  
  @media (max-width: 768px) {
    margin: 0.75rem 0;
    text-align: center;
    justify-content: center;
  }
`;

const MenuLink = styled(Link)`
  color: var(--text);
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 0;
  transition: color 0.3s ease;
  display: block;
  
  &:hover, &.active {
    color: var(--accent-turquoise);
  }
`;

const DropdownButton = styled.div`
  color: var(--text);
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 0;
  transition: color 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  &:hover {
    color: var(--accent-turquoise);
  }
  
  &:after {
    content: "▼";
    display: inline-block;
    font-size: 0.7em;
    margin-left: 0.5rem;
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  }
`;

const Dropdown = styled.ul`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: absolute;
  top: 100%;
  left: 0;
  background-color: white;
  min-width: 200px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 0.5rem 0;
  z-index: 10;
  
  @media (max-width: 768px) {
    position: static;
    box-shadow: none;
    margin: 0.5rem 0;
    width: 100%;
  }
`;

const DropdownItem = styled.li`
  list-style: none;
  padding: 0;
  
  &:hover {
    background-color: var(--secondary);
  }
  
  a {
    padding: 0.75rem 1.5rem;
    display: block;
    width: 100%;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  color: var(--text);
  font-size: 1.5rem;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  const toggleServicesDropdown = () => {
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
  };
  
  return (
    <HeaderContainer>
      <Nav>
        <Logo to="/">Passport <span>Creative</span></Logo>
        
        <MobileMenuButton onClick={toggleMenu}>
          ☰
        </MobileMenuButton>
        
        <MenuItems isOpen={isMenuOpen}>
          <MenuItem>
            <DropdownButton 
              onClick={toggleServicesDropdown}
              isOpen={isServicesDropdownOpen}
            >
              Services
            </DropdownButton>
            <Dropdown isOpen={isServicesDropdownOpen}>
              <DropdownItem>
                <MenuLink to="/services">All Services</MenuLink>
              </DropdownItem>
              <DropdownItem>
                <MenuLink to="/services/travel-marketing">Travel Marketing</MenuLink>
              </DropdownItem>
              <DropdownItem>
                <MenuLink to="/services/trade-show-support">Trade Show Support</MenuLink>
              </DropdownItem>
              <DropdownItem>
                <MenuLink to="/services/monthly-marketing-support">Monthly Marketing Support</MenuLink>
              </DropdownItem>
              <DropdownItem>
                <MenuLink to="/services/tourism-development">Tourism Development</MenuLink>
              </DropdownItem>
              <DropdownItem>
                <MenuLink to="/services/digital-tools">Digital Tools</MenuLink>
              </DropdownItem>
            </Dropdown>
          </MenuItem>
          <MenuItem>
            <MenuLink to="/projects">Projects</MenuLink>
          </MenuItem>
          <MenuItem>
            <MenuLink to="/tools">Tools</MenuLink>
          </MenuItem>
          <MenuItem>
            <MenuLink to="/about">About</MenuLink>
          </MenuItem>
          <MenuItem>
            <MenuLink to="/contact">Contact</MenuLink>
          </MenuItem>
          <MenuItem>
            <MenuLink to="/blog">Blog</MenuLink>
          </MenuItem>
        </MenuItems>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;